import { useState, useEffect, useCallback } from "react";
import { apiFetch, ApiError } from "@/lib/api-client";
import type { FiscalYearSummary, FiscalYearsResponse } from "../types";

interface UseFiscalYearsResult {
  years: FiscalYearSummary[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Hook to fetch fiscal years.
 */
export function useFiscalYears(): UseFiscalYearsResult {
  const [years, setYears] = useState<FiscalYearSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [fetchKey, setFetchKey] = useState(0);

  useEffect(() => {
    const fetchYears = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const data = await apiFetch.get<FiscalYearsResponse>(
          "/api/fiscal-calendar/years",
        );
        setYears(data.years);
      } catch (err) {
        if (err instanceof ApiError) {
          setError(err);
        } else {
          setError(err instanceof Error ? err : new Error("Unknown error"));
        }
        setYears([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchYears();
  }, [fetchKey]);

  const refetch = useCallback(() => setFetchKey((k) => k + 1), []);

  return {
    years,
    isLoading,
    error,
    refetch,
  };
}
